import { useCallback, useEffect, useRef, useState } from "react";

type Options = {
    hideDelay?: number;
    enabled?: boolean;
};

export const usePresentationControlsVisibility = ({
    hideDelay = 2000,
    enabled = true,
}: Options = {}) => {
    const [isVisible, setVisible] = useState(true);
    const hideTimeout = useRef<number | null>(null);

    const clearHideTimer = useCallback(() => {
        if (hideTimeout.current) {
            clearTimeout(hideTimeout.current);
            hideTimeout.current = null;
        }
    }, []);

    const startHideTimer = useCallback(() => {
        if (!enabled) return;

        clearHideTimer();
        hideTimeout.current = window.setTimeout(() => {
            setVisible(false);
        }, hideDelay);
    }, [clearHideTimer, hideDelay, enabled]);

    const showControls = useCallback(() => {
        if (!enabled) return;

        setVisible(true);
        startHideTimer();
    }, [enabled, startHideTimer]);

    const onControlsMouseEnter = useCallback(() => {
        clearHideTimer();
    }, [clearHideTimer]);

    const onControlsMouseLeave = useCallback(() => {
        startHideTimer();
    }, [startHideTimer]);

    useEffect(() => {
        if (!enabled) return;

        const handleKeyDown = () => {
            showControls();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [enabled, showControls]);

    useEffect(() => () => clearHideTimer(), [clearHideTimer]);

    return {
        isControlsVisible: isVisible,
        showControls,
        onControlsMouseEnter,
        onControlsMouseLeave,
    };
};
